import useSWR from 'swr'
import { useSession } from 'next-auth/react'
import {
  BoolQueryV1,
  MultiMatchQueryV1,
  QueryV1,
  RangeQueryV1,
  SortingV1,
  TermsQueryV1
} from '@ttab/elephant-api/index'
import type { HitV1 } from '@ttab/elephant-api/index'
import { startOfDay } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { useRegistry } from '@/hooks/useRegistry'
import { fetch } from '@/hooks/index/useDocuments/lib/fetch'
import { toInsertedMemoDocument } from '@/shared/templates/memos/lib/insertedDocument'
import type { InsertedMemoDocument } from '@/shared/templates/memos/lib/insertedDocument'
import type { MemoInsertableDocumentsFilter } from '@/shared/templates/memos'

/**
 * When the document was published. A document that never has been carries no
 * value here at all, which is what makes a range on it a test of "has gone out"
 * as much as of when.
 */
const PUBLISHED_FIELD = 'heads.usable.created'

/**
 * Unpublishing keeps the usable head, creation time and all, at this version -
 * so a withdrawn PM still falls inside the range, and is left out on its own.
 */
const USABLE_VERSION_FIELD = 'heads.usable.version'
const UNPUBLISHED_VERSION = '-1'

/**
 * The largest page the index loads whole documents for. The day's PMs are a
 * handful, so this is a ceiling rather than something to page past.
 */
const RESULT_SIZE = 200

/**
 * The PMs that have gone out today: published no earlier than this morning and
 * no later than right now.
 *
 * Only published ones are offered. A memo is written to restate a PM its readers
 * have already had, and an unpublished one is a colleague's work in progress -
 * folding that in would put text into a memo before whoever is writing it has
 * decided it is finished. It is also the one test that does not depend on
 * reading a timestamp correctly: the field is simply absent until a document has
 * been published. One that has since been unpublished is excluded by version.
 *
 * The upper bound is `now` rather than the end of the day, because a document
 * held for later publication carries the time it is *scheduled* to go out. Next
 * week's weekly PM, written today and held until its own day, would otherwise
 * count as published the moment it was scheduled.
 *
 * `title`, when given, also requires the title to contain it as a phrase prefix.
 */
export function insertableDocumentsQuery(from: Date, until: Date, title?: string): QueryV1 {
  const published = QueryV1.create({
    conditions: {
      oneofKind: 'range',
      range: RangeQueryV1.create({
        field: PUBLISHED_FIELD,
        gte: from.toISOString(),
        lte: until.toISOString()
      })
    }
  })

  const unpublished = QueryV1.create({
    conditions: {
      oneofKind: 'terms',
      terms: TermsQueryV1.create({ field: USABLE_VERSION_FIELD, values: [UNPUBLISHED_VERSION] })
    }
  })

  const titled = title
    ? [QueryV1.create({
        conditions: {
          oneofKind: 'multiMatch',
          multiMatch: MultiMatchQueryV1.create({
            fields: ['document.title'],
            query: title,
            type: 'phrase_prefix'
          })
        }
      })]
    : []

  return QueryV1.create({
    conditions: {
      oneofKind: 'bool',
      bool: BoolQueryV1.create({
        must: [published, ...titled],
        mustNot: [unpublished]
      })
    }
  })
}

/**
 * The editorial-info documents published today, most recently first, whole -
 * the PMs the creation dialog offers to fold into the one being written. See
 * `insertableDocumentsQuery` for what counts as one of today's.
 *
 * The documents themselves are loaded with the hits rather than fetched when
 * one is picked: a day's worth of PMs is a small enough result to ask for whole,
 * and inserting one is then a click rather than a click and a wait.
 *
 * `useDocuments` is not used here for the same reason `useMemoCandidates` does
 * not - it requires a TableProvider ancestor, which the creation dialog has no
 * part in.
 *
 * `enabled` is how the dialog holds the query back for a template that does not
 * offer insertion; nothing is fetched and nothing is cached until then. `today`
 * is `YYYY-MM-DD` in `timeZone`, passed in rather than read from the clock here
 * so that the query is not a new one on every render. `filter` is the
 * template's `insertableDocumentsFilter`, narrowing by title and count.
 */
export function useInsertableMemoDocuments(
  today: string,
  timeZone: string,
  filter: MemoInsertableDocumentsFilter | undefined,
  enabled = true
): {
  documents: InsertedMemoDocument[]
  isLoading: boolean
  error: Error | undefined
} {
  const { index, repository } = useRegistry()
  const { data: session } = useSession()

  const title = filter?.title
  const latestOnly = filter?.latestOnly ?? false

  const key = session?.accessToken && enabled
    ? ['memo-insertable-documents', today, timeZone, title ?? '', latestOnly]
    : null

  const { data, error, isLoading } = useSWR<InsertedMemoDocument[], Error>(key, async () => {
    // Midnight of the day in the registry's timezone, as the instant it is -
    // comparing a timestamp against a bare `YYYY-MM-DD` would mean midnight UTC,
    // and so name a different moment for most of the world. The other end is
    // simply now: nothing has gone out later than that.
    const from = fromZonedTime(startOfDay(new Date(`${today}T00:00:00`)), timeZone)

    const hits = await fetch<HitV1, readonly string[]>({
      index,
      repository,
      session,
      documentType: 'core/editorial-info',
      fields: ['document.title', PUBLISHED_FIELD],
      query: insertableDocumentsQuery(from, new Date(), title),
      // Most recently published first. Every document that matched has the
      // field, so there is no unpublished one to sort into an odd place.
      sort: [SortingV1.create({ field: PUBLISHED_FIELD, desc: true })],
      size: latestOnly ? 1 : RESULT_SIZE,
      loadDocument: true
    })

    return hits
      .map((hit) => toInsertedMemoDocument(hit))
      .filter((document): document is InsertedMemoDocument => !!document)
  })

  return {
    documents: data ?? [],
    isLoading,
    error
  }
}
